import { useCallback, useMemo, useState } from "react";
import {
  RegisterOptions,
  SubmitHandler,
  useForm,
  useFormState,
  useWatch,
} from "react-hook-form";

import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  FormPanel,
  LabelRow,
} from "../../../../components/FormPanel";
import {
  FormInput,
  FormMoneyInput,
  InputDate,
  SelectOptionTypes,
} from "../../../../components/Input";
import { buildUnsavedChangesDetails } from "../../../../components/PopUp/confirmShared";
import { localToday } from "../../../../utils";
import {
  tradeDateFloor,
  cashbackCappedAmountRules,
  validateQuantity,
  validateTradeDate,
} from "../tradeValidation";
import { CashbackFundingFields } from "../CashbackFundingFields";
import { CASHBACK_CORRECTION_AMOUNT_HELPER } from "../constants";
import { ChargeField } from "../ChargeField";
import { InterestDirectionField } from "../InterestDirectionField";
import { NoteField } from "../NoteField";
import { centsOf, settlementNoteOf } from "../settlementNote";
import { useCashbackFunding } from "../useCashbackFunding";
import { InterestDirection } from "../types";
import { MobileTradeCorrectionForm } from "./MobileTradeCorrectionForm";
import {
  acceptsCashback,
  acceptsCharge,
  acceptsQuantity,
  chargeOf,
  buildContextLine,
  correctionCopyFor,
  correctionNetCentsOf,
  createDefaultCorrection,
  FORM_ID,
  taxedIncomeOf,
} from "./constants";
import { TradeCorrectionFormValues, TradeCorrectionPanelProps } from "./types";
import { ContextCard, ContextHint, ContextLine } from "./styles";

// A sibling of `HoldingTradePanel` rather than a mode of it: a correction locks the type and the
// position, so a type control or a position select would suggest a move the server cannot make.
export const TradeCorrectionPanel = ({
  isOpen,
  onClose,
  command,
  investmentName,
  investmentStartDate,
  investmentAccountId,
  settlementAccountName,
  handleOnSubmit,
}: TradeCorrectionPanelProps) => {
  const [panelError, setPanelError] = useState<string | null>(null);

  const showsQuantity = acceptsQuantity(command);
  const charge = chargeOf(command);
  const taxedIncome = taxedIncomeOf(command);
  const copy = correctionCopyFor(command);
  // The gate is the label's presence, not a second predicate: only the types
  // `acceptsDirection` accepts carry one, so the copy map cannot drift from it.
  const directionGroupLabel = copy.directionGroupLabel;
  const contextLine = buildContextLine(command);

  const form = useForm<TradeCorrectionFormValues>({
    defaultValues: createDefaultCorrection(command),
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, setError, setValue } = form;

  const { isSubmitting, dirtyFields, errors } = useFormState({ control });
  const direction = useWatch({ control, name: "direction" });
  const showsCharge = acceptsCharge(command, direction);
  const [amountEur, feeEur, taxWithheld, date, cashbackEur, cashbackSource] =
    useWatch({
      control,
      name: [
        "amountEur",
        "feeEur",
        "taxWithheld",
        "date",
        "cashbackEur",
        "cashbackSource",
      ],
    });
  const showsCashback = acceptsCashback(command);
  const settlementNote = settlementNoteOf({
    accountName: settlementAccountName,
    netCents: correctionNetCentsOf(command, {
      amountEur,
      feeEur,
      taxWithheld,
      direction,
      cashbackEur,
    }),
    isCashbackFunded: showsCashback && centsOf(cashbackEur) > 0,
  });

  const setCashbackSource = useCallback(
    (option: SelectOptionTypes | null) => setValue("cashbackSource", option),
    [setValue],
  );
  const cashback = useCashbackFunding({
    isEnabled: showsCashback,
    investmentAccountId,
    cashbackSource,
    recorded: command.cashback,
    setCashbackSource,
  });

  const minDate = useMemo(
    () => tradeDateFloor(investmentStartDate),
    [investmentStartDate],
  );

  const handleDirectionChange = (next: InterestDirection) => {
    setValue("direction", next, { shouldDirty: true });
  };

  const onSubmit: SubmitHandler<TradeCorrectionFormValues> = (data) => {
    setPanelError(null);
    return handleOnSubmit(data, { setError, setPanelError });
  };

  // An edit form tracks changed fields rather than a draft record, so the discard summary counts them.
  const buildDiscardDetails = () =>
    buildUnsavedChangesDetails(Object.keys(dirtyFields).length);

  const dateRules: RegisterOptions<TradeCorrectionFormValues, "date"> = {
    required: "Date is required!",
    validate: (value: Date) => validateTradeDate(value, minDate),
  };

  // Buy and sell only: a cash-only command never sends a quantity, so the rule
  // must not block its submit with a value the payload will not even carry.
  // Owned here and threaded through, exactly as `dateRules` is: desktop and
  // compact have to gate the quantity on the same command type.
  const quantityRules: RegisterOptions<TradeCorrectionFormValues, "quantity"> =
    {
      validate: (value: string) =>
        showsQuantity ? validateQuantity(value) : true,
    };

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      error={panelError}
      ariaLabel={copy.ariaLabel}
      eyebrow={copy.eyebrow}
      title={`Correct ${copy.headerNoun} in ${investmentName}`}
      form={form}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel={copy.submitLabel}
      footerNote={settlementNote}
      discard={{
        title: copy.discardTitle,
        confirmLabel: "Discard",
        details: buildDiscardDetails,
      }}
      mobile={{
        title: copy.compactTitle,
        submitLabel: copy.compactActionLabel,
        fields: (
          <MobileTradeCorrectionForm
            control={control}
            copy={copy}
            contextLine={contextLine}
            showsQuantity={showsQuantity}
            showsCharge={showsCharge}
            directionGroupLabel={directionGroupLabel}
            direction={direction}
            directionError={errors.direction?.message}
            onDirectionChange={handleDirectionChange}
            charge={charge}
            taxedIncome={taxedIncome}
            minDate={minDate}
            dateRules={dateRules}
            quantityRules={quantityRules}
            cashback={cashback}
            isSubmitting={isSubmitting}
          />
        ),
      }}
    >
      <ContextCard>
        <ContextLine>{contextLine}</ContextLine>
        <ContextHint>{copy.contextHint}</ContextHint>
      </ContextCard>

      {directionGroupLabel && (
        <FormCard>
          <InterestDirectionField
            value={direction}
            groupLabel={directionGroupLabel}
            isSubmitting={isSubmitting}
            error={errors.direction?.message}
            onChange={handleDirectionChange}
          />
        </FormCard>
      )}

      {showsQuantity && (
        <FormCard>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="trade-correction-quantity">
                Quantity *
              </FieldLabel>
            </LabelRow>
            <FormInput
              id="trade-correction-quantity"
              required
              name="quantity"
              control={control}
              rules={quantityRules}
              handleClear={false}
              disabled={isSubmitting}
              placeholder="e.g. 12.5"
            />
            <FieldHelper>Units traded, up to 6 decimal places.</FieldHelper>
          </Field>
        </FormCard>
      )}

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="trade-correction-amount">Amount *</FieldLabel>
          </LabelRow>
          <FormMoneyInput
            id="trade-correction-amount"
            required
            name="amountEur"
            control={control}
            rules={cashbackCappedAmountRules}
            disabled={isSubmitting}
            placeholder="0.00"
          />
          <FieldHelper>{copy.amountHelper}</FieldHelper>
        </Field>

        {showsCharge && (
          <ChargeField
            id="trade-correction-fee"
            control={control}
            charge={charge}
            helper={copy.chargeHelper}
            isSubmitting={isSubmitting}
            taxedIncome={taxedIncome}
          />
        )}

        {cashback.isShown && (
          <CashbackFundingFields
            idPrefix="trade-correction"
            control={control}
            sourceOptions={cashback.sourceOptions}
            amountHelper={CASHBACK_CORRECTION_AMOUNT_HELPER}
            isSubmitting={isSubmitting}
          />
        )}
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="trade-correction-date">
              Trade date *
            </FieldLabel>
          </LabelRow>
          <InputDate
            id="trade-correction-date"
            name="date"
            control={control}
            rules={dateRules}
            disabled={isSubmitting}
            minDate={minDate}
            maxDate={localToday()}
            placeholder="YYYY/MM/DD"
          />
          <FieldHelper>
            Must be on or after the account's start date.
          </FieldHelper>
        </Field>

        <NoteField
          id="trade-correction-note"
          control={control}
          isSubmitting={isSubmitting}
        />
      </FormCard>
    </FormPanel>
  );
};
